import { BaseApplicant as Applicant, isNonEmployedJobType } from '@applyonline/shared'
import { formatCurrency, formatPhone } from '@if/core/utils'
import { first } from 'lodash-es'
import { useTranslation } from 'react-i18next'

interface Props {
  applicant: Applicant
  hasApplicantError: boolean
  hasApplicantAddressError: boolean
  hasApplicantRevenueError: boolean
  gotoStep: (target: string) => void
}
const ReviewApplicantInfo = ({
  applicant,
  hasApplicantError,
  hasApplicantAddressError,
  hasApplicantRevenueError,
  gotoStep,
}: Props) => {
  const { t } = useTranslation()
  const yearsAtCurrentAddress =
    applicant.currentAddress.months < 12 ? -1 : Math.floor(applicant.currentAddress.months / 12)
  return (
    <>
      <div className="review-item">
        <h1 className="h3">{t('common.aboutYourself')}</h1>
        <div className="box">
          <div className="box-content">
            <p>
              <strong>{t('personalInformation.preferredLanguage')}&nbsp;: </strong>
              <span>{t(`ELanguage.${applicant.languageId}`, { ns: 'enum' })}</span>
            </p>
            <p>
              <strong>{t('personalInformation.yourGender')}&nbsp;: </strong>
              <span>{t(`EGender.${applicant.genderId}`, { ns: 'enum' })}</span>
            </p>
            <p>
              <strong>{t('personalInformation.yourFirstName')}&nbsp;: </strong>
              <span>{applicant.firstName}</span>
            </p>
            <p>
              <strong>{t('personalInformation.yourLastName')}&nbsp;: </strong>
              <span>{applicant.lastName}</span>
            </p>
            <p>
              <strong>{t('confirmation.birthDate')}&nbsp;: </strong>
              <span>{applicant.birthDate}</span>
            </p>
            <p>
              <strong>{t('confirmation.mobilePhone')}&nbsp;: </strong>
              <span>{formatPhone(applicant.cellPhone)}</span>
            </p>
            {applicant.homePhone && (
              <p>
                <strong>{t('confirmation.homePhone')}&nbsp;: </strong>
                <span>{formatPhone(applicant.homePhone)}</span>
              </p>
            )}
            <p>
              <strong>{t('confirmation.email')}&nbsp;: </strong>
              <span>{applicant.email}</span>
            </p>
            {applicant.sin && (
              <p>
                <strong>{t('personalInformation.sin')}&nbsp;: </strong>
                <span>{applicant.sin}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            className={
              hasApplicantError
                ? 'btn-error btn-simple-error align-left-mobile'
                : 'btn-action btn-simple align-left-mobile'
            }
            onClick={() => gotoStep('personal-information')}
          >
            {t('review.modify')}
          </button>
        </div>
      </div>

      <div className="review-item">
        <h1 className="h3">{t('common.yourHome')}</h1>
        <div className="box">
          <div className="box-content">
            <p>
              <strong>{t('confirmation.address')}&nbsp;: </strong>
              <span>
                {applicant.currentAddress.apartment && `${applicant.currentAddress.apartment}-`}
                {applicant.currentAddress.civicNumber} {applicant.currentAddress.street},{' '}
                {applicant.currentAddress.city}, {applicant.currentAddress.stateIso}{' '}
                {applicant.currentAddress.postalCode}
              </span>
            </p>
            <p>
              <strong>{t('common.since')}&nbsp;: </strong>
              {yearsAtCurrentAddress === -1 ? (
                <span>{t('common.lessThanOneYear')}</span>
              ) : (
                <span>{t('common.yearsLabel', { count: yearsAtCurrentAddress })}&nbsp;</span>
              )}
            </p>
            <p>
              <strong>{t('housing.homeFeeType')}&nbsp;: </strong>
              <span>{t(`EHomeStatus.${applicant.spending.homeFeeTypeId}`, { ns: 'enum' })}</span>
            </p>
          </div>
          <button
            type="button"
            className={
              hasApplicantAddressError
                ? 'btn-error btn-simple-error align-left-mobile'
                : 'btn-action btn-simple align-left-mobile'
            }
            onClick={() => gotoStep('address')}
          >
            {t('review.modify')}
          </button>
        </div>
      </div>

      {!!applicant.currentJobs?.[0] && (
        <div className="review-item">
          <h1 className="h3">{t('income.howMuch')}</h1>
          <div className="box">
            <div className="box-content">
              {!isNonEmployedJobType(applicant.currentJobs[0].jobType) && (
                <>
                  <p>
                    <strong>{t('income.category')}&nbsp;: </strong>
                    <span> {t(`EJobType.${applicant.currentJobs[0].jobType}`, { ns: 'enum' })}</span>
                  </p>
                  <p>
                    <strong>{t('income.jobTitle')}&nbsp;: </strong>
                    <span> {applicant.currentJobs[0].jobTitle}</span>
                  </p>
                  <p>
                    <strong>{t('income.beforeDeductions')}&nbsp;: </strong>
                    <span> {formatCurrency(applicant.currentJobs[0].annualSalary)}</span>
                  </p>
                  <p>
                    <strong>{t('income.employerName')}&nbsp;: </strong>
                    <span> {applicant.currentJobs[0].employerName}</span>
                  </p>
                  <p>
                    <strong>{t('income.phoneNumber')}&nbsp;: </strong>
                    <span> {formatPhone(applicant.currentJobs[0].employerPhone)}</span>
                  </p>
                  <p>
                    <strong>{t('income.extension')}&nbsp;: </strong>
                    <span> {applicant.currentJobs[0].employerPhoneExt}</span>
                  </p>

                  <p>
                    <strong>{t('income.employmentLenght')}&nbsp;: </strong>
                    {applicant.currentJobs[0].years === -1 ? (
                      <span>{t('common.lessThanOneYear')}</span>
                    ) : (
                      applicant.currentJobs[0].years && (
                        <span>
                          {t('common.yearsLabel', {
                            count: applicant.currentJobs[0].years,
                          })}
                          &nbsp;
                        </span>
                      )
                    )}
                  </p>
                </>
              )}
              <p>
                <strong>{t('income.otherIncome')}&nbsp;: </strong>
                <span> {formatCurrency(first(applicant.otherIncomes)?.annualAmount)}</span>
              </p>
            </div>
            <button
              type="button"
              className={
                hasApplicantRevenueError
                  ? 'btn-error btn-simple-error align-left-mobile'
                  : 'btn-action btn-simple align-left-mobile'
              }
              onClick={() => gotoStep('employment')}
            >
              {t('review.modify')}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default ReviewApplicantInfo
